// Pure rules for purchase order lines (no imports, so Node's test runner loads it as is).
// A line is a product in one order unit; messages are English i18n keys.

export const ORDER_LINE_MSG = {
  unitTaken: "This product is already on the order in this unit",
} as const;

export type Capacity = "low" | "medium" | "max";

const list = (value: unknown): any[] => (Array.isArray(value) ? value : []);
const text = (value: unknown): string => (value === null || value === undefined ? "" : String(value));
const amount = (value: unknown): number => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};
const positive = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

/* ---------------- Identity ---------------- */

/** product|unit: no two lines of one order or template share it. */
export const lineKey = (productId: unknown, productUOMId: unknown): string => `${text(productId)}|${text(productUOMId)}`;

/** React key and per-line state key: the saved line's id, else the key it got when it was added. */
export const rowKey = (line: any): string =>
  line?.id ? `id:${line.id}` : line?.clientKey ? String(line.clientKey) : `new:${lineKey(line?.productId, line?.productUOMId)}`;

/** A key for a line added on screen that no other line uses. */
export const newClientKey = (productId: unknown, productUOMId: unknown, taken: Set<string>): string => {
  const base = `new:${lineKey(productId, productUOMId)}`;
  let key = base;
  for (let n = 2; taken.has(key); n++) key = `${base}#${n}`;
  return key;
};

/** Another line of the product already uses this unit. */
export const unitTaken = (lines: unknown, productId: unknown, productUOMId: unknown, exceptRowKey?: string): boolean =>
  list(lines).some(
    (line) => rowKey(line) !== exceptRowKey && lineKey(line?.productId, line?.productUOMId) === lineKey(productId, productUOMId),
  );

/** True when two lines share a product and unit. */
export const hasDuplicateLines = (lines: unknown): boolean => {
  const keys = list(lines).map((line) => lineKey(line?.productId, line?.productUOMId));
  return new Set(keys).size !== keys.length;
};

/* ---------------- Units ---------------- */

export const unitOf = (units: unknown, productUOMId: unknown): any =>
  (text(productUOMId) && list(units).find((unit) => unit?.id && text(unit.id) === text(productUOMId))) || null;

export const baseUnitOf = (units: unknown): any => list(units).find((unit) => unit?.isBase) ?? null;

/** A line without a unit is in the product's base unit. */
export const unitIdOrBase = (productUOMId: unknown, units: unknown): string => text(productUOMId) || text(baseUnitOf(units)?.id);

/** The unit's own factor, sent as conversionFactorUsed; 1 when missing or not positive. */
export const unitFactor = (units: unknown, productUOMId: unknown): number => positive(unitOf(units, productUOMId)?.conversionFactor) ?? 1;

/** Base units in one line unit, the ratio deliveries use; 1 when anything is missing or not positive. */
export const baseUnitsPerUnit = (units: unknown, productUOMId: unknown): number => {
  const lineFactor = positive(unitOf(units, productUOMId)?.conversionFactor);
  const baseFactor = positive(baseUnitOf(units)?.uom?.conversionFactor);
  return lineFactor && baseFactor ? lineFactor / baseFactor : 1;
};

/** Stock (base units) at or below Reorder at, which counts in the line's own unit. */
export const isDueForReorder = (stock: unknown, lowQty: unknown, units: unknown, productUOMId: unknown): boolean =>
  amount(stock) <= amount(lowQty) * baseUnitsPerUnit(units, productUOMId);

/** Line units that bring stock (base units) up to `level` (line units): whole units, rounded up; 0 when there is enough. */
export const qtyToLevel = (level: unknown, stock: unknown, units: unknown, productUOMId: unknown): number => {
  const needed = amount(level) - amount(stock) / baseUnitsPerUnit(units, productUOMId);
  return needed > 1e-9 ? Math.ceil(needed - 1e-9) : 0;
};

/* ---------------- Price ---------------- */

/** Cost of one line unit from a supplier: its price for that unit, else unitCost × the unit's factor; null without a row. */
export const supplierUnitCost = (supplierInventory: unknown, clientId: unknown, productUOMId: unknown, factor: unknown): number | null => {
  if (!text(clientId)) return null;
  const rows = list(supplierInventory).filter((row) => row && text(row.clientId) === text(clientId));
  if (!rows.length) return null;
  const own = text(productUOMId) ? rows.find((row) => text(row.productUOMId) === text(productUOMId)) : undefined;
  if (own && positive(own.cost) !== null) return Number(own.cost);
  const row = own ?? rows.find((candidate) => !candidate.productUOMId) ?? rows[0];
  return amount(row.unitCost) * (positive(factor) ?? 1);
};

/* ---------------- Template lines ---------------- */

const slim = (line: any) => ({
  id: line.id,
  productId: text(line.productId),
  productUOMId: line.productUOMId ?? null,
  lowQty: line.lowQty,
  medQty: line.medQty,
  highQty: line.highQty,
});

/** Adds template lines not known yet, matched by id, else by product and unit. */
export const mergeTemplateLines = (known: any[], incoming: unknown): any[] => {
  const fresh = list(incoming).filter((line) => line?.productId);
  if (!fresh.length) return known;
  const out = [...known];
  for (const line of fresh) {
    const at = out.findIndex((other) =>
      line.id && other.id ? other.id === line.id : lineKey(other.productId, other.productUOMId) === lineKey(line.productId, line.productUOMId),
    );
    if (at === -1) out.push(slim(line));
    else out[at] = slim(line);
  }
  return out;
};

/** The template line for a product in one unit; a template line without a unit is in the base unit. */
export const templateLineFor = (templateLines: unknown, productId: unknown, productUOMId: unknown, units: unknown): any => {
  const unit = unitIdOrBase(productUOMId, units);
  return (
    list(templateLines).find(
      (line) => line && text(line.productId) === text(productId) && unitIdOrBase(line.productUOMId, units) === unit,
    ) ?? null
  );
};

/**
 * A template line with its levels in another of the product's units: 2 packs of 50 are 100 pieces. Max counts in whole
 * line units, rounded up, so the line can always reach Minimum. null when either unit or its factor is missing.
 */
export const levelsInUnit = (templateLine: any, units: unknown, productUOMId: unknown): any => {
  const from = positive(unitOf(units, unitIdOrBase(templateLine?.productUOMId, units))?.conversionFactor);
  const to = positive(unitOf(units, unitIdOrBase(productUOMId, units))?.conversionFactor);
  if (from === null || to === null) return null;
  const convert = (value: unknown) => (amount(value) * from) / to;
  const max = convert(templateLine?.highQty);
  return {
    ...templateLine,
    // A copy for this line only: it is not the template line and is never merged back.
    id: undefined,
    productUOMId: text(productUOMId) || null,
    lowQty: convert(templateLine?.lowQty),
    medQty: convert(templateLine?.medQty),
    highQty: max > 0 ? Math.ceil(max - 1e-9) : 0,
    /** The template's own line, in its own unit. */
    source: templateLine,
  };
};

const factorRank = (templateLine: any, units: unknown): number => {
  const unit = unitOf(units, unitIdOrBase(templateLine?.productUOMId, units));
  return unit?.isBase ? 0 : positive(unit?.conversionFactor) ?? Infinity;
};

/**
 * The template line an order line counts against. Each of the product's template lines serves one order line: the line
 * in its unit first; the rest go to the product's other lines in order, the base unit's template line first, then the
 * smaller units. A line left without one has none.
 */
export const templateSourceOf = (line: any, templateLines: unknown, orderLines?: unknown): any => {
  const units = line?.product?.productUOMs;
  const unitOfLine = (other: any) => unitIdOrBase(other?.productUOMId, units);
  const sameProduct = (other: any) => text(other?.productId) === text(line?.productId);
  // The product's lines in the order's order, this one in its place (one line per unit), else last.
  const productLines = list(orderLines).filter(sameProduct);
  const at = productLines.findIndex((other) => unitOfLine(other) === unitOfLine(line));
  const all = at === -1 ? [...productLines, line] : productLines.map((other, index) => (index === at ? line : other));
  // The known template lines, then the ones the API attached; one per unit, the first found wins.
  const candidates: any[] = [];
  for (const templateLine of [...list(templateLines).filter(sameProduct), ...all.map((other) => other?.orderTemplateDetail)]) {
    if (!templateLine || (!templateLine.id && !templateLine.productId)) continue;
    if (candidates.some((known) => unitIdOrBase(known.productUOMId, units) === unitIdOrBase(templateLine.productUOMId, units))) continue;
    candidates.push(templateLine);
  }
  const ownOf = (other: any) => candidates.find((templateLine) => unitIdOrBase(templateLine.productUOMId, units) === unitOfLine(other)) ?? null;
  const own = ownOf(line);
  if (own) return own;
  const claimed = new Set(all.map(ownOf).filter(Boolean));
  const free = candidates
    .filter((templateLine) => !claimed.has(templateLine) && unitOf(units, unitIdOrBase(templateLine.productUOMId, units)))
    .sort((a, b) => factorRank(a, units) - factorRank(b, units) || text(a.id).localeCompare(text(b.id)));
  return free[all.filter((other) => !ownOf(other)).indexOf(line)] ?? null;
};

/** The template line in the line's own unit: as it is in that unit, else converted; null when it cannot be. */
export const inLineUnit = (templateLine: any, line: any): any => {
  if (!templateLine) return null;
  const units = line?.product?.productUOMs;
  return unitIdOrBase(templateLine.productUOMId, units) === unitIdOrBase(line?.productUOMId, units) ? templateLine : levelsInUnit(templateLine, units, line?.productUOMId);
};

/** An order line's template line with its levels in the line's own unit; null when the template does not have it for this line. */
export const templateLineOfLine = (line: any, templateLines: unknown, orderLines?: unknown): any => inLineUnit(templateSourceOf(line, templateLines, orderLines), line);

/** The template's own Reorder at and Max for a line in another unit, with that unit's name; null for a line in the template's unit. */
export const templateLevelsText = (line: any, templateLine: any): { low: string; max: string | null; unit: string } | null => {
  const source = templateLine?.source;
  if (!source) return null;
  const max = amount(source.highQty);
  return {
    low: qtyText(source.lowQty),
    max: max > 0 ? qtyText(max) : null,
    unit: text(unitOf(line?.product?.productUOMs, unitIdOrBase(source.productUOMId, line?.product?.productUOMs))?.uom?.uomName),
  };
};

/** True when the template has the product at all, also in a unit another line of the order uses. */
export const productOnTemplate = (line: any, templateLines: unknown): boolean =>
  list(templateLines).some((templateLine) => templateLine && text(templateLine.productId) === text(line?.productId)) ||
  !!(line?.orderTemplateDetail && (line.orderTemplateDetail.id || line.orderTemplateDetail.productId));

/** Order qty a capacity button gives; Maximum falls back to the order qty when Max is 0; null without a template line. */
export const capacityQty = (templateLine: any, capacity: Capacity): number | null => {
  if (!templateLine) return null;
  if (capacity === "low") return amount(templateLine.lowQty);
  if (capacity === "medium") return amount(templateLine.medQty);
  return amount(templateLine.highQty) || amount(templateLine.medQty);
};

/** Max of the line's template line; 0, or no template line in that unit, means no limit. */
export const maxQtyOf = (templateLine: any): number => amount(templateLine?.highQty);

export const exceedsMax = (orderQty: unknown, templateLine: any): boolean => {
  const max = maxQtyOf(templateLine);
  return max > 0 && amount(orderQty) > max;
};

/* ---------------- Picking products for an order ---------------- */

/** The units a picked product adds: one per template line, else its base unit. */
export const pickUnits = (pick: any): { productUOMId: string; templateLine: any }[] => {
  const units = pick?.product?.productUOMs;
  // Older API answers carry only the first template line, spread on the pick itself.
  const templateLines = Array.isArray(pick?.orderTemplateDetails) ? pick.orderTemplateDetails : pick?.inTemplate ? [pick] : [];
  const out: { productUOMId: string; templateLine: any }[] = [];
  for (const templateLine of templateLines) {
    const productUOMId = unitIdOrBase(templateLine?.productUOMId, units);
    if (out.some((entry) => entry.productUOMId === productUOMId)) continue;
    out.push({ productUOMId, templateLine });
  }
  if (!out.length) out.push({ productUOMId: text(baseUnitOf(units)?.id), templateLine: null });
  return out;
};

/** True when ticking the product would add a line: a unit it adds is not on the order yet. */
export const pickAddsLines = (pick: any, lines: unknown): boolean =>
  pickUnits(pick).some(({ productUOMId }) => !unitTaken(lines, pick?.productId, productUOMId));

export interface PickOptions {
  clientId: unknown;
  capacity: Capacity;
  // Cost of one unit when the supplier has no price row for the product.
  fallbackCost?: (pick: any, factor: number) => number;
}

/** New order lines for a picked product: one per unit it adds that the order does not have yet. */
export const linesForPick = (pick: any, existing: unknown, options: PickOptions): any[] => {
  const lines = list(existing);
  const product = pick?.product ?? {};
  const units = product.productUOMs;
  const keys = new Set(lines.map(rowKey));
  const out: any[] = [];
  for (const { productUOMId, templateLine } of pickUnits(pick)) {
    if (unitTaken([...lines, ...out], pick?.productId, productUOMId)) continue;
    const factor = unitFactor(units, productUOMId);
    const cost =
      supplierUnitCost(product.supplierInventory, options.clientId, productUOMId, factor) ??
      (options.fallbackCost ? amount(options.fallbackCost(pick, factor)) : 0);
    const orderQty = templateLine ? capacityQty(templateLine, options.capacity) ?? 0 : amount(pick?.lowQty);
    const clientKey = newClientKey(pick?.productId, productUOMId, keys);
    keys.add(clientKey);
    out.push({
      clientKey,
      product,
      productId: pick?.productId,
      orderQty,
      cost,
      unitCost: cost / factor,
      lineCost: cost * orderQty,
      currentStock: pick?.currentStock,
      orderTemplateDetail: templateLine ? slim({ ...templateLine, productId: pick?.productId }) : null,
      deliveryQty: 0,
      varianceQty: 0,
      taxId: product.productPrices?.[0]?.tax?.id || "",
      inTemplate: pick?.inTemplate,
      productUOMId,
      conversionFactorUsed: factor,
      uomName: text(unitOf(units, productUOMId)?.uom?.uomName),
      isAttributes: true,
    });
  }
  return out;
};

/** The template lines a page of picker rows carries. */
export const templateLinesOfPicks = (picks: unknown): any[] =>
  list(picks).flatMap((pick) =>
    pickUnits(pick)
      .filter((entry) => entry.templateLine)
      .map((entry) => ({ ...entry.templateLine, productId: entry.templateLine.productId ?? pick?.productId })),
  );

/* ---------------- The order page: editable lines, checks, totals, payload ---------------- */

export interface EditLine {
  key: string;
  /** The saved line's id; a line added on the page has none yet. */
  id?: string;
  productId: string;
  product: any;
  productUOMId: string;
  conversionFactorUsed: number;
  /** Price of one base-factor unit, for a unit change without a supplier price. */
  unitCost: number;
  qty: string;
  /** Price per order unit (order_details.cost). */
  price: string;
  deliveryQty: number;
  varianceQty: number;
  currentStock: number;
  orderTemplateDetail: any | null;
}

const finite = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** null for an empty field, NaN for text that is not a number; a decimal comma counts as a point. */
export const parseNumber = (text: unknown): number | null => {
  const value = String(text ?? '').trim().replace(',', '.');
  return value === '' ? null : Number(value);
};

export const qtyText = (value: unknown): string => {
  const n = Number(value);
  return Number.isFinite(n) ? String(Math.round(n * 10000) / 10000) : '';
};

/** "3.60" for cents, "0.425" when the saved price has more decimals, so a save never rounds it. */
export const priceText = (value: unknown): string => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return Math.abs(n * 100 - Math.round(n * 100)) < 1e-9 ? n.toFixed(2) : String(n);
};

export const round2 = (value: number): number => Math.round(finite(value) * 100) / 100;

export const unitNameOf = (line: { product?: any; productUOMId?: string }): string => unitOf(line.product?.productUOMs, line.productUOMId)?.uom?.uomName ?? '';

export const vatRateOf = (line: { product?: any }): number => finite(line.product?.productPrices?.[0]?.tax?.primaryRate);

/** A line from the order API: prices per order unit, as the page has always read them. */
export const toEditLine = (detail: any): EditLine => {
  const conversion = finite(detail?.conversionFactorUsed);
  const cost = finite(detail?.cost);
  return {
    key: rowKey(detail),
    id: detail?.id,
    productId: String(detail?.productId ?? ''),
    product: detail?.product ?? {},
    productUOMId: detail?.productUOMId ?? '',
    conversionFactorUsed: conversion,
    unitCost: conversion > 0 ? cost / conversion : cost,
    qty: qtyText(detail?.orderQty),
    price: priceText(cost),
    deliveryQty: finite(detail?.deliveryQty),
    varianceQty: finite(detail?.varianceQty),
    currentStock: finite(detail?.currentStock ?? detail?.product?.remainingQtySum),
    orderTemplateDetail: detail?.orderTemplateDetail ?? null,
  };
};

/**
 * A picked product as a new line in one unit; it starts at what brings stock up to the template's usual level
 * (counted in this unit when the template has the product in another), else empty.
 */
export const lineForPick = (pick: any, productUOMId: string, clientId: unknown, takenKeys: Set<string>, orderLines?: unknown): EditLine => {
  const product = pick?.product ?? {};
  const units = product.productUOMs;
  const factor = unitFactor(units, productUOMId);
  // Without a supplier price, the product's price per base unit as before.
  const cost = supplierUnitCost(product.supplierInventory, clientId, productUOMId, factor) ?? finite(product.productPrices?.[0]?.cost) * factor;
  const asLine = { productId: pick?.productId, productUOMId, product };
  const source = templateSourceOf(asLine, pick?.orderTemplateDetails ?? [], orderLines);
  const templateLine = inLineUnit(source, asLine);
  const usual = templateLine ? fillQty(templateLine, 'medium', pick?.currentStock, units, productUOMId) : 0;
  return {
    key: newClientKey(pick?.productId, productUOMId, takenKeys),
    productId: String(pick?.productId ?? ''),
    product,
    productUOMId,
    conversionFactorUsed: factor,
    unitCost: factor > 0 ? cost / factor : cost,
    qty: usual > 0 ? qtyText(usual) : '',
    price: priceText(cost),
    deliveryQty: 0,
    varianceQty: 0,
    currentStock: finite(pick?.currentStock),
    // The template's own line, in its unit; the page converts it for this line.
    orderTemplateDetail: source ? { ...source, productId: pick?.productId } : null,
  };
};

/** The line in another unit: the supplier's price for it, else the unit cost × the unit's factor. */
export const withUnit = (line: EditLine, productUOMId: string, clientId: unknown, supplierRows: unknown): EditLine => {
  const factor = unitFactor(line.product?.productUOMs, productUOMId);
  const cost = supplierUnitCost(supplierRows, clientId, productUOMId, factor) ?? factor * line.unitCost;
  return { ...line, productUOMId, conversionFactorUsed: factor, price: priceText(cost) };
};

/** What brings stock up to a template level, in whole line units, never past the template's Max. */
const fillQty = (templateLine: any, capacity: Capacity, stock: unknown, units: unknown, productUOMId: unknown): number => {
  const qty = qtyToLevel(capacityQty(templateLine, capacity), stock, units, productUOMId);
  const max = maxQtyOf(templateLine);
  return max > 0 ? Math.min(qty, max) : qty;
};

/**
 * A Fill quantities choice: what brings the line's stock up to that template level. A line with enough stock,
 * or without a template line for it, keeps its own quantity.
 */
export const filledQty = (line: EditLine, templateLine: any, capacity: Capacity): string => {
  if (!templateLine) return line.qty;
  const qty = fillQty(templateLine, capacity, line.currentStock, line.product?.productUOMs, line.productUOMId);
  return qty > 0 ? qtyText(qty) : line.qty;
};

export interface LineCheck {
  /** Shown under the field; an empty field only says so after a save was tried. */
  qtyError: string;
  priceError: string;
  /** Amber note under the price, e.g. "Was €3.60". */
  priceWas: number | null;
  qtyMax: number;
  amount: number | null;
  /** Something blocks saving, shown or not. */
  hard: boolean;
  blocking: boolean;
}

export const QTY_MSG = {
  empty: 'Enter a quantity',
  nan: 'Enter a number',
  notPositive: 'Must be over 0',
  max: 'Max {max}',
} as const;

export const PRICE_MSG = {
  empty: 'Enter a unit price',
  nan: 'Enter a number',
  negative: "Can't be negative",
} as const;

export const checkLine = (line: EditLine, templateLine: any, saved: EditLine | undefined, flagged: boolean): LineCheck => {
  const q = parseNumber(line.qty);
  const p = parseNumber(line.price);
  let qtyError = '';
  if (q === null) qtyError = QTY_MSG.empty;
  else if (Number.isNaN(q)) qtyError = QTY_MSG.nan;
  else if (q <= 0) qtyError = QTY_MSG.notPositive;
  else if (exceedsMax(q, templateLine)) qtyError = QTY_MSG.max;
  let priceError = '';
  if (p === null) priceError = PRICE_MSG.empty;
  else if (Number.isNaN(p)) priceError = PRICE_MSG.nan;
  else if (p < 0) priceError = PRICE_MSG.negative;
  const hard = qtyError !== '' || priceError !== '';
  const shownQty = q === null && !flagged ? '' : qtyError;
  const shownPrice = p === null && !flagged ? '' : priceError;
  const was = saved && saved.productUOMId === line.productUOMId ? parseNumber(saved.price) : null;
  const priceWas = !priceError && was !== null && Number.isFinite(was) && p !== null && Math.abs(was - p) > 0.0001 ? was : null;
  return {
    qtyError: shownQty,
    priceError: shownPrice,
    priceWas,
    qtyMax: maxQtyOf(templateLine),
    amount: q !== null && Number.isFinite(q) && q > 0 && p !== null && Number.isFinite(p) && p >= 0 ? q * p : null,
    hard,
    blocking: shownQty !== '' || shownPrice !== '',
  };
};

/** Gross line values with VAT included at each product's rate, as the order's stored tax has always been worked out. */
export const totalsOf = (rows: { amount: number | null; rate: number }[]) => {
  const total = rows.reduce((sum, row) => sum + (row.amount ?? 0), 0);
  const net = rows.reduce((sum, row) => sum + (row.amount ?? 0) / (1 + row.rate / 100), 0);
  const vat = round2(total - net);
  const rates = [...new Set(rows.filter((row) => (row.amount ?? 0) > 0).map((row) => row.rate))];
  return { subtotal: round2(round2(total) - vat), vat, total: round2(total), rate: rates.length === 1 ? rates[0] : null };
};

export type Change = { kind: 'added' | 'edited' | 'removed'; key: string };

/** What differs from the saved order, line by line. */
export const changesOf = (lines: EditLine[], saved: EditLine[]): Change[] => {
  const out: Change[] = [];
  for (const line of lines) {
    const before = saved.find((other) => other.key === line.key);
    if (!before) out.push({ kind: 'added', key: line.key });
    else if (
      before.productUOMId !== line.productUOMId ||
      String(before.qty).trim() !== String(line.qty).trim() ||
      parseNumber(before.price) !== parseNumber(line.price)
    )
      out.push({ kind: 'edited', key: line.key });
  }
  for (const before of saved) if (!lines.some((line) => line.key === before.key)) out.push({ kind: 'removed', key: before.key });
  return out;
};

/** The line's name, with its unit when the product has another line too. */
export const labelOf = (line: EditLine, lines: EditLine[]): string => {
  const name = line.product?.productName ?? '';
  return lines.filter((other) => other.productId === line.productId).length > 1 ? `${name} ${unitNameOf(line)}`.trim() : name;
};

/**
 * The PUT /orders/:id body. Lines at 0 are left out (the API drops them), line_cost keeps its stored
 * convention (factor × price × qty), amount and tax follow the page's gross line values.
 */
export const orderPayload = (order: any, lines: EditLine[], statusId: number, companyId: string) => {
  const kept = lines
    .map((line) => ({ line, qty: finite(parseNumber(line.qty)), cost: finite(parseNumber(line.price)) }))
    .filter((entry) => entry.qty > 0);
  const amount = kept.reduce((sum, entry) => sum + entry.cost * entry.qty, 0);
  const delivery = kept.reduce((sum, entry) => sum + entry.line.deliveryQty, 0);
  const net = kept.reduce((sum, entry) => sum + (entry.cost * entry.qty) / (1 + vatRateOf(entry.line) / 100), 0);
  return {
    clientId: order?.clientId || '',
    internalNumber: order?.internalNumber || 0,
    companyId,
    comment: order?.comment || '',
    // The order's own site: saving never moves it to the site picked in the header.
    locationId: order?.locationId || order?.location?.id || '',
    statusId,
    amount,
    delivery,
    tax: Number((amount - net).toFixed(2)),
    orderTemplateId: order?.orderTemplateId || '',
    orderDetails: kept.map(({ line, qty, cost }) => ({
      // A saved line keeps its id, so a unit change updates it instead of replacing it.
      ...(line.id ? { id: line.id } : {}),
      productId: line.productId,
      orderQty: qty,
      deliveryQty: line.deliveryQty,
      varianceQty: line.varianceQty,
      cost,
      lineCost: line.conversionFactorUsed * cost * qty,
      conversionFactorUsed: line.conversionFactorUsed,
      productUOMId: line.productUOMId || '',
    })),
  };
};
